import { Trans } from "@lingui/react/macro";
import Container from "../components/Container.tsx";
import ActionButton from "../components/ActionButton.tsx";
import {
  LucideIcon,
  ShieldCheck,
  Globe,
  CreditCard,
  Music2,
} from "lucide-react";
import {
  getInstrumentIcon,
  getTranslation,
  useSupportedInstruments,
} from "../api/instrumentApi.ts";
import { useLanguageStore } from "../stores/languageStore.ts";
import { Link } from "react-router";
import GradientHeader from "../components/util/GradientHeader.tsx";

export const Home = () => {
  function handleBecomeTeacherClick() {
    const teacherSection = document.getElementById("teacher-section");
    teacherSection?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function handleStudentSetupClick() {
    const studentSection = document.getElementById("student-section");
    studentSection?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  const { language } = useLanguageStore();
  const { data: instruments } = useSupportedInstruments();

  return (
    <div className="relative min-h-[calc(100vh-8rem)]">
      {/* SECTION 1: Hero and Highlights */}
      <section className="mx-auto min-h-[calc(100vh-4rem)] max-w-6xl px-4 pt-12 pb-16 lg:min-h-auto">
        {/* Hero */}
        <div className="mb-14 text-center">
          {/* Subtle pulsing music note instead of bouncing arrows */}
          <div className="mb-6 flex justify-center">
            <Music2
              className="text-soluud-primary h-10 w-10 animate-pulse opacity-60"
              aria-hidden="true"
            />
          </div>

          <GradientHeader>
            <Trans>soluud. your one stop shop for music lessons.</Trans>
          </GradientHeader>
          <p className="text-soluud-text-secondary mx-auto mb-8 max-w-2xl text-base sm:text-lg">
            <Trans>
              Search by instrument, location and language. Chat, book, and learn
              — all in one place.
            </Trans>
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <ActionButton
              type="button"
              variant="secondary"
              onClick={handleStudentSetupClick}
            >
              <Trans>How to find your teacher</Trans>
            </ActionButton>
            <ActionButton
              type="button"
              variant="primary"
              onClick={handleBecomeTeacherClick}
            >
              <Trans>How to become a teacher</Trans>
            </ActionButton>
          </div>
        </div>

        {/* Highlights */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <Container className="hover:shadow-[--shadow-card-hover]">
            <div className="accent-line mb-4 w-12" />
            <div className="mb-3 flex items-center gap-3">
              <div className="bg-soluud-primary-light flex h-10 w-10 items-center justify-center rounded-lg">
                <ShieldCheck className="text-soluud-primary h-5 w-5" />
              </div>
              <h3 className="text-base font-semibold">
                <Trans>Verified teachers</Trans>
              </h3>
            </div>
            <p className="text-soluud-text-secondary text-sm leading-relaxed">
              <Trans>
                Browse detailed profiles with languages, locations, and
                availability to find your perfect match.
              </Trans>
            </p>
          </Container>
          <Container className="hover:shadow-[--shadow-card-hover]">
            <div className="accent-line mb-4 w-12" />
            <div className="mb-3 flex items-center gap-3">
              <div className="bg-soluud-primary-light flex h-10 w-10 items-center justify-center rounded-lg">
                <Globe className="text-soluud-primary h-5 w-5" />
              </div>
              <h3 className="text-base font-semibold">
                <Trans>Remote or In-Person</Trans>
              </h3>
            </div>
            <p className="text-soluud-text-secondary text-sm leading-relaxed">
              <Trans>
                Take lessons online via video or meet locally at your preferred
                location — whatever fits your schedule and learning style.
              </Trans>
            </p>
          </Container>
          <Container className="hover:shadow-[--shadow-card-hover]">
            <div className="accent-line mb-4 w-12" />
            <div className="mb-3 flex items-center gap-3">
              <div className="bg-soluud-primary-light flex h-10 w-10 items-center justify-center rounded-lg">
                <CreditCard className="text-soluud-primary h-5 w-5" />
              </div>
              <h3 className="text-base font-semibold">
                <Trans>Secure checkout</Trans>
              </h3>
            </div>
            <p className="text-soluud-text-secondary text-sm leading-relaxed">
              <Trans>
                Pay safely and get instant confirmations for every booking.
              </Trans>
            </p>
          </Container>
        </div>
      </section>

      {/* Section divider */}
      <div className="accent-line mx-auto w-full max-w-6xl" />

      {/* SECTION: Instruments */}
      <section id="instruments-section" className="py-16">
        <div className="mx-auto max-w-6xl px-4">
          <div className="mb-8 text-center">
            <h2 className="from-soluud-primary to-soluud-secondary animated-gradient-text bg-gradient-to-r bg-clip-text text-2xl font-extrabold tracking-tight text-transparent sm:text-3xl">
              <Trans>Search for the instrument you want to learn</Trans>
            </h2>
            <p className="text-soluud-text-secondary mt-3 text-sm sm:text-base">
              <Trans>
                You can easily search for your desired instrument. We even have
                way more filters for you on the next page so you can find
                exactly what you want and need.
              </Trans>
            </p>
          </div>
          <div className="grid w-full grid-cols-3 gap-4 sm:grid-cols-6">
            {instruments
              ?.slice(0, 6)
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((instrument) => {
                const IconComponent: LucideIcon = getInstrumentIcon(
                  instrument.name,
                );
                return (
                  <Link
                    to={`/search?instrument=${instrument.name}`}
                    key={instrument.name}
                    className="border-soluud-border-subtle bg-soluud-background-card hover:border-soluud-primary flex aspect-square cursor-pointer flex-col items-center justify-center rounded-xl border p-4 text-center shadow-[--shadow-card] transition-all duration-200 hover:shadow-[--shadow-card-hover] hover:[&_svg]:scale-110 hover:[&_svg]:rotate-6"
                  >
                    <div className="text-soluud-text-primary mb-2 transition-transform duration-200">
                      <IconComponent size={32} />
                    </div>
                    <span className="text-soluud-text-primary text-xs font-medium sm:text-sm">
                      {getTranslation(instrument, language)}
                    </span>
                  </Link>
                );
              })}
          </div>
        </div>
      </section>

      {/* Section divider */}
      <div className="accent-line mx-auto w-full max-w-6xl" />

      {/* SECTION: For Students */}
      <section id="student-section" className="py-16">
        <div className="mx-auto max-w-6xl px-4">
          <div className="mb-8 text-center">
            <h2 className="from-soluud-primary to-soluud-secondary animated-gradient-text bg-gradient-to-r bg-clip-text text-2xl font-extrabold tracking-tight text-transparent sm:text-3xl">
              <Trans>For students: three easy steps</Trans>
            </h2>
            <p className="text-soluud-text-secondary mt-3 text-sm sm:text-base">
              <Trans>
                Find the teacher you are looking for and get started or refine
                your musical skills.
              </Trans>
            </p>
          </div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
            <Container className="transition-shadow duration-200 hover:shadow-[--shadow-card-hover]">
              <div className="mb-3 flex items-center gap-3">
                <span className="bg-soluud-primary text-soluud-text-light inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold shadow-sm">
                  1
                </span>
                <h3 className="text-base font-semibold">
                  <Trans>Find your teacher</Trans>
                </h3>
              </div>
              <p className="text-soluud-text-secondary text-sm leading-relaxed">
                <Trans>
                  Search by instrument, location and language. Explore profiles
                  and reviews.
                </Trans>
              </p>
            </Container>
            <Container className="transition-shadow duration-200 hover:shadow-[--shadow-card-hover]">
              <div className="mb-3 flex items-center gap-3">
                <span className="bg-soluud-secondary text-soluud-text-on-secondary inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold shadow-sm">
                  2
                </span>
                <h3 className="text-base font-semibold">
                  <Trans>Book a time</Trans>
                </h3>
              </div>
              <p className="text-soluud-text-secondary text-sm leading-relaxed">
                <Trans>
                  Pick a bunch of timeslots to book or a regular schedule.
                </Trans>
              </p>
            </Container>
            <Container className="transition-shadow duration-200 hover:shadow-[--shadow-card-hover]">
              <div className="mb-3 flex items-center gap-3">
                <span className="bg-soluud-hint text-soluud-text-light inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold shadow-sm">
                  3
                </span>
                <h3 className="text-base font-semibold">
                  <Trans>Pay as you go</Trans>
                </h3>
              </div>
              <p className="text-soluud-text-secondary text-sm leading-relaxed">
                <Trans>
                  Pay conveniently online. Automatic refunds for canceled
                  lessons.
                </Trans>
              </p>
            </Container>
          </div>
        </div>
      </section>

      {/* Section divider */}
      <div className="accent-line mx-auto w-full max-w-6xl" />

      {/* SECTION: For Teachers */}
      <section id="teacher-section" className="py-16">
        <div className="mx-auto max-w-6xl px-4">
          <div className="mb-8 text-center">
            <h2 className="from-soluud-secondary to-soluud-primary animated-gradient-text bg-gradient-to-r bg-clip-text text-2xl font-extrabold tracking-tight text-transparent sm:text-3xl">
              <Trans>Setup steps for teachers</Trans>
            </h2>
            <p className="text-soluud-text-secondary mt-3 text-sm sm:text-base">
              <Trans>
                Soluud brings you new students, easy scheduling, and
                frictionless payments.
              </Trans>
            </p>
          </div>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
            <Container className="transition-shadow duration-200 hover:shadow-[--shadow-card-hover]">
              <div className="mb-3 flex items-center gap-3">
                <span className="bg-soluud-primary text-soluud-text-light inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold shadow-sm">
                  1
                </span>
                <h3 className="text-base font-semibold">
                  <Trans>Create your profile</Trans>
                </h3>
              </div>
              <p className="text-soluud-text-secondary text-sm leading-relaxed">
                <Trans>
                  Add instruments, locations and languages. Set lesson types and
                  pricing.
                </Trans>
              </p>
            </Container>
            <Container className="transition-shadow duration-200 hover:shadow-[--shadow-card-hover]">
              <div className="mb-3 flex items-center gap-3">
                <span className="bg-soluud-secondary text-soluud-text-on-secondary inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold shadow-sm">
                  2
                </span>
                <h3 className="text-base font-semibold">
                  <Trans>Setup Payment Information</Trans>
                </h3>
              </div>
              <p className="text-soluud-text-secondary text-sm leading-relaxed">
                <Trans>
                  Setup an account with our payment provider to get payouts.
                </Trans>
              </p>
            </Container>
            <Container className="transition-shadow duration-200 hover:shadow-[--shadow-card-hover]">
              <div className="mb-3 flex items-center gap-3">
                <span className="bg-soluud-hint text-soluud-text-light inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold shadow-sm">
                  3
                </span>
                <h3 className="text-base font-semibold">
                  <Trans>Teach and get paid</Trans>
                </h3>
              </div>
              <p className="text-soluud-text-secondary text-sm leading-relaxed">
                <Trans>
                  Chat with students, run lessons online or in-person, and
                  receive payouts with fair cancellation policies.
                </Trans>
              </p>
            </Container>
          </div>
        </div>
      </section>
    </div>
  );
};
